'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';

export function Done2GoLogo({ height = 36 }: {height?: number;}) {
  return (
    <img
      src="/assets/images/logo_transparente-1781823931240-1781877190928.png"
      alt="Done2Go logo"
      style={{ height, width: 'auto', display: 'block', objectFit: 'contain' }}
    />
  );
}

export function Ico({ d, size = 24 }: {d: string;size?: number;}) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d={d} />
    </svg>);

}

export function CheckCircle({ size = 20 }: {size?: number;}) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none">
      <circle cx="10" cy="10" r="10" fill="#F26522" opacity="0.12" />
      <path d="M6 10l3 3 5-5" stroke="#F26522" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>);

}

export function Star() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="#F26522">
      <path d="M8 1l1.8 3.6L14 5.3l-3 2.9.7 4.1L8 10.4l-3.7 1.9.7-4.1-3-2.9 4.2-.7L8 1z" />
    </svg>);

}

export function useReveal() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {if (entry.isIntersecting) {el.classList.add('visible');obs.disconnect();}},
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

export function useRevealStagger() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {if (entry.isIntersecting) {el.classList.add('visible');obs.disconnect();}},
      { threshold: 0.08 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

export function useRevealLeft() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {if (entry.isIntersecting) {el.classList.add('visible');obs.disconnect();}},
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

export function useRevealRight() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {if (entry.isIntersecting) {el.classList.add('visible');obs.disconnect();}},
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

export function useRevealScale() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {if (entry.isIntersecting) {el.classList.add('visible');obs.disconnect();}},
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

export function useCounter(target: number, duration = 1800) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const started = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        const start = performance.now();
        const step = (now: number) => {
          const p = Math.min((now - start) / duration, 1);
          const e = 1 - Math.pow(1 - p, 3);
          setCount(Math.round(e * target));
          if (p < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
        obs.disconnect();
      }
    }, { threshold: 0.3 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [target, duration]);
  return { ref, count };
}

export function FAQItem({ q, a }: {q: string;a: string;}) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ border: '1px solid #e4eaf0', borderRadius: 18, overflow: 'hidden', background: '#fff' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '20px 28px', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>
        
        <span style={{ fontWeight: 600, fontSize: 16, color: '#1F2933', lineHeight: 1.4 }}>{q}</span>
        <span style={{ flexShrink: 0, width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: open ? '#F26522' : '#f1f5f8', color: open ? '#fff' : '#5b6b78', transition: 'all 0.3s', transform: open ? 'rotate(180deg)' : 'rotate(0)' }}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M3 5l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>
        </span>
      </button>
      <div style={{ maxHeight: open ? 400 : 0, overflow: 'hidden', transition: 'max-height 0.4s cubic-bezier(0.25,0.46,0.45,0.94)' }}>
        <p style={{ padding: '0 28px 20px', fontSize: 15, color: '#5b6b78', lineHeight: 1.7 }}>{a}</p>
      </div>
    </div>);

}

export const INDUSTRIES = [
{ label: 'Restaurant', title: 'Speisekarte & Reservierung', desc: 'Menu, Oeffnungszeiten und Tischreservierung - appetitlich praesentiert.', img: "https://images.unsplash.com/photo-1676327679378-1a3ca1040924", alt: 'Gemuetliches Restaurant mit gedeckten Tischen' },
{ label: 'Friseur & Beauty', title: 'Online-Terminbuchung', desc: 'Leistungen, Preise und direkte Buchung - ohne Telefonschleife.', img: "https://img.rocket.new/generatedImages/rocket_gen_img_1ab368934-1776768694308.png", alt: 'Moderner Friseursalon mit Styling-Stuehlen' },
{ label: 'Handwerk', title: 'Leistungen & Anfrage', desc: 'Referenzprojekte und ein klarer Weg zur unverbindlichen Anfrage.', img: "https://images.unsplash.com/photo-1628839835275-2b24c0596853", alt: 'Handwerker bei der Arbeit auf einer Baustelle' },
{ label: 'Werkstatt', title: 'Service & Terminannahme', desc: 'Servicefragen beantwortet, Termine strukturiert entgegengenommen.', img: "https://images.unsplash.com/photo-1620555790872-4b4eeeddcb8d", alt: 'Kfz-Werkstatt mit Fahrzeugen auf Hebebuehnen' },
{ label: 'Beauty-Studio', title: 'Behandlungen & Galerie', desc: 'Aesthetische Galerie, klare Preise, direkte Kontaktaufnahme.', img: "https://img.rocket.new/generatedImages/rocket_gen_img_18d54ed92-1773179538975.png", alt: 'Elegantes Beauty-Studio mit Behandlungsliege' },
{ label: 'Arztpraxis', title: 'Praxisinfos & Sprechzeiten', desc: 'Sprechzeiten, Leistungen und Erreichbarkeit - serioes und klar.', img: "https://img.rocket.new/generatedImages/rocket_gen_img_1a6c7a814-1781265744878.png", alt: 'Helle moderne Arztpraxis mit Empfangsbereich' }];


export const TESTIMONIALS = [
{ name: 'Maria Hofmann', role: 'Inhaberin, Salon Hofmann', text: 'Ich habe den Link bekommen und war sofort begeistert. Keine langen Gespraeche, kein Hin und Her - einfach eine fertige Website, die zu meinem Salon passt. Innerhalb von 15 Tagen war alles live.', stars: 5, initials: 'MH' },
{ name: 'Thomas Berger', role: 'Geschaeftsfuehrer, Berger Sanitaer', text: 'Als Handwerker hat man keine Zeit fuer Briefings und Agenturgespräche. Done2Go hat einfach gebaut - und das Ergebnis war besser als erwartet. Jetzt kommen Anfragen direkt ueber die Website.', stars: 5, initials: 'TB' },
{ name: 'Ayse Kaya', role: 'Inhaberin, Beauty by Ayse', text: 'Endlich eine Website, die mein Studio wirklich repraesentiert. Der Preis war transparent, keine versteckten Kosten. Das Hosting-Abo laeuft problemlos - ich kuemmere mich um nichts.', stars: 5, initials: 'AK' }];


export const FAQS = [
{ q: 'Wie koennen Sie eine Website bauen, bevor wir gesprochen haben?', a: 'Wir nutzen oeffentlich verfuegbare Informationen ueber Ihren Betrieb und unsere Erfahrung mit Ihrer Branche, um ein realistisches Demo-Modell zu erstellen. Es ist ein durchdachter Startpunkt - die finalen Inhalte stimmen wir gemeinsam ab, sobald Sie sich fuer eine Zusammenarbeit entscheiden.' },
{ q: 'Ist das wirklich risikofrei?', a: 'Ja. Fuer die Demo entstehen Ihnen keine Kosten und keine Verpflichtung. Erst wenn Sie die Seite kaufen moechten, nennen wir den Preis - transparent, bevor Sie zusagen. Die Demo selbst ist immer kostenlos.' },
{ q: 'Was passiert nach den 15 Tagen?', a: 'Die Chance erlischt. Wir verkaufen jede Demo nur einmal - an den Betrieb, fuer den sie gebaut wurde. Meldet sich niemand innerhalb von 15 Tagen, wird das Projekt nicht mehr angeboten. Das schafft Klarheit fuer beide Seiten.' },
{ q: 'Was kostet die Website genau?', a: 'Website One-Page: 349 EUR. Website Complete (mit Terminbuchung, Google-Profil etc.): 499 EUR. Hosting Basis: 14,99 EUR/Monat. Hosting + Pflege: 24,99 EUR/Monat. KI-Assistent (200 WhatsApp-Nachrichten/Monat): 99 EUR/Monat. Alle Preise werden transparent kommuniziert - vor jeder Entscheidung.' },
{ q: 'Wie schnell ist die Website live?', a: 'Sofort. Bei unserem Hosting-Paket ist die Seite direkt live - kein Warten. Moechten Sie lieber Ihren eigenen Server nutzen, erhalten Sie alle Dateien zur sofortigen Installation.' },
{ q: 'Kann ich meinen eigenen Hosting-Anbieter nutzen?', a: 'Ja. Sie koennen die fertige Website auch auf Ihrem eigenen Hosting betreiben. Unser Pflege-Abo ist ein Angebot, keine Pflicht.' },
{ q: 'Ist der KI-Assistent (WhatsApp) schon verfuegbar?', a: 'Der digitale Empfang befindet sich in Vorbereitung. Sie koennen sich jetzt auf die Warteliste setzen lassen und gehoeren zu den Ersten, die ihn einsetzen koennen.' },
{ q: 'Betreuen Sie auch Betriebe ausserhalb Deutschlands?', a: 'Ja. Unser Schwerpunkt liegt auf dem deutschsprachigen Raum, aber unser Modell funktioniert grenzueberschreitend fuer Betriebe in Deutschland, Oesterreich und der Schweiz.' }];


export const MARQUEE_ITEMS = ['Friseure & Salons', 'Gastronomie', 'Handwerk', 'Beauty & Wellness', 'Arzt- & Zahnarztpraxen', 'Lokale Dienstleister', 'Werkstaetten', 'Boutiquen & Shops', 'Yoga & Fitness', 'Immobilien'];